<script setup>
// Stores
import { useModalStore } from '@/stores/modal.js'
import { useI18n } from 'vue-i18n'
import { computed, onMounted } from 'vue'
import Login from '@/components/modals/login.vue'
import SignUp from '@/components/modals/sign_up.vue'
import TermsAndConditions from '@/components/modals/terms_and_conditions.vue'
import ForgotPassword from '@/components/modals/forgot_password.vue'
import AddVehicle from '@/components/modals/garage/add_vehicle.vue'
import AddCompletedService from '@/components/modals/garage/add_completed_service.vue'
import GarageInfo from '@/components/modals/garage/garage_info.vue'
import VehicleInfo from '@/components/modals/garage/vehicle_info.vue'
import DrivetrainInfo from '@/components/modals/garage/drivetrain_info.vue'
import DesignInfo from '@/components/modals/garage/design_info.vue'
import UserAddress from '@/components/modals/user_address.vue'
import DeleteAccount from "@/components/modals/delete_account.vue"
import SignUpVerification from "@/components/modals/sign_up_verification.vue"
import ResendVerification from "@/components/modals/resend_verification.vue"
import ChangePasswordConfirmationNotice from "@/components/modals/change_password_confirmation_notice.vue"
import StripePayment from "@/components/modals/stripe_payment.vue"
import ConfirmMoveToServiceHistory from "@/components/modals/confirm_move_to_service_history.vue"

// I18n
const { t } = useI18n()

// Stores
const modalStore = useModalStore()

onMounted(async () => {
})

const positiveButtonDisabled = computed(() => {
  switch (modalStore.modalType) {
    case 'case_1':
      return false
    default:
      return false
  }
})

const currentModalSmall = () => {
  if (modalStore.smallWidthModals.includes(modalStore.modalType)) {
    return 'min-[300px]:mx-auto min-[300px] sm:max-w-[95%] md:max-w-[75%] lg:max-w-[50%] xl:max-w-[40%]'
  } else {
    return 'min-[300px]:mx-auto min-[300px] sm:max-w-[95%] md:max-w-[85%] lg:max-w-[80%] xl:max-w-[75%]'
  }
}
</script>

<template>
  <div
    v-if="modalStore.modalVisibility"
    data-te-modal-init
    class="fixed inset-0 flex items-center justify-center w-full items-center z-[1000]"
    id="exampleModalCenter"
    tabindex="-1"
    aria-labelledby="exampleModalCenterTitle"
    aria-modal="true"
    role="dialog"
    :class="{ 'fade-in': modalStore.modalVisibility }">
    <div class="z-20 fixed inset-0 bg-black opacity-50 overflow-auto"></div>
    <div
      data-te-modal-init
      :class="`relative w-full z-30 fixed inset-0 flex items-center justify-center opacity-100 transition-all duration-300 ease-in-out ${ currentModalSmall() }`">
      <div
        data-te-modal-dialog-ref
        class="pointer-events-none relative w-full opacity-100 transition-all duration-300 ease-in-out min-[300px]:mx-auto max-w-[90%]">
        <div
          class="min-[300px]:shadow-[0_0.5rem_1rem_rgba(#000, 0.15)] pointer-events-auto relative flex w-full flex-col rounded-md border-none bg-white bg-clip-padding text-current shadow-lg outline-none dark:bg-neutral-600">
          <div
            class="flex flex-shrink-0 items-center justify-between rounded-t-md py-3 px-4 modal-header">
            <!--Modal title-->
            <h5
              class="text-xl font-semibold">
              {{ modalStore.modalTitle }}
            </h5>
            <!--Close button-->
            <button
              type="button"
              class="box-content rounded-none border-none hover:no-underline hover:opacity-75 focus:opacity-100 focus:shadow-none focus:outline-none"
              data-te-modal-dismiss
              aria-label="Close"
              @click="modalStore.negativeHandler"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="h-6 w-6">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!--Modal body-->
          <div v-if="modalStore.modalBody" class="relative flex-auto text-md p-4" data-te-modal-body-ref
               v-html="modalStore.modalBody"></div>

          <!-- Error messages -->
          <div v-if="modalStore.modalErrors && modalStore.modalErrors.length > 0"
               class="bg-red-100 mx-4 mb-2 border border-red-400 text-red-700 px-4 py-3 rounded relative mx-8 md:mx-16 mt-4 md:mt-8"
               role="alert">
            <ul class="list-disc ml-5 text-sm">
              <li v-for="(error, index) in modalStore.modalErrors" :key="index">{{ error }}</li>
            </ul>
          </div>

          <!--Modal Components-->
          <div class="max-h-[75vh] overflow-y-scroll sm:overflow-auto">
            <Login v-if="modalStore.modalType === modalStore.modalTypeConstants.LOGIN" />
            <SignUp :class="{'hidden': modalStore.modalType === modalStore.modalTypeConstants.TERMS_AND_CONDITIONS}"
                    v-if="modalStore.modalType === modalStore.modalTypeConstants.SIGN_UP || modalStore.modalType === modalStore.modalTypeConstants.TERMS_AND_CONDITIONS" />
            <TermsAndConditions v-if="modalStore.modalType === modalStore.modalTypeConstants.TERMS_AND_CONDITIONS" />
            <ForgotPassword v-if="modalStore.modalType === modalStore.modalTypeConstants.FORGOT_PASSWORD" />
            <AddVehicle
              v-if="modalStore.modalType === modalStore.modalTypeConstants.ADD_VEHICLE || modalStore.modalType === modalStore.modalTypeConstants.EDIT_VEHICLE" />
            <GarageInfo v-if="modalStore.modalType === modalStore.modalTypeConstants.GARAGE_INFO" />
            <VehicleInfo v-if="modalStore.modalType === modalStore.modalTypeConstants.VEHICLE_INFO" />
            <DrivetrainInfo v-if="modalStore.modalType === modalStore.modalTypeConstants.DRIVETRAIN_INFO" />
            <DesignInfo v-if="modalStore.modalType === modalStore.modalTypeConstants.DESIGN_INFO" />
            <UserAddress
              v-if="modalStore.modalType === modalStore.modalTypeConstants.ADD_ADDRESS || modalStore.modalType === modalStore.modalTypeConstants.EDIT_ADDRESS" />
            <AddCompletedService
              v-if="modalStore.modalType === modalStore.modalTypeConstants.ADD_COMPLETED_SERVICE"></AddCompletedService>
              <DeleteAccount v-if="modalStore.modalType === modalStore.modalTypeConstants.DELETE_ACCOUNT"></DeleteAccount>
              <SignUpVerification v-if="modalStore.modalType === modalStore.modalTypeConstants.SIGN_UP_VERIFICATION"></SignUpVerification>
              <ResendVerification v-if="modalStore.modalType === modalStore.modalTypeConstants.RESEND_VERIFICATION"></ResendVerification>
              <ChangePasswordConfirmationNotice v-if="modalStore.modalType === modalStore.modalTypeConstants.CHANGE_PASSWORD_CONFIRMATION_NOTICE"></ChangePasswordConfirmationNotice>
              <StripePayment v-if="modalStore.modalType === modalStore.modalTypeConstants.STRIPE_PAYMENT"></StripePayment>
              <ConfirmMoveToServiceHistory v-if="modalStore.modalType === modalStore.modalTypeConstants.CONFIRM_MOVE_TO_SERVICE_HISTORY"></ConfirmMoveToServiceHistory>

          </div>

          <!--Modal footer-->
          <div
            v-if="modalStore.negativeButtonText || modalStore.positiveButtonText"
            class="flex flex-shrink-0 flex-wrap items-center justify-between rounded-b-md border-t-2 border-neutral-100 border-opacity-100 p-4 dark:border-opacity-50">
            <div class="flex flex-row hover:opacity-75">
              <img
                v-if="modalStore.backButtonText"
                @click="modalStore.setModal(modalStore.backButtonLink)"
                src="../assets/icons/chevron_left_24dp_FILL0_wght400_GRAD0_opsz24.svg" alt="Logo 1"
                class="cursor-pointer">
              <button
                v-if="modalStore.backButtonText"
                type="button"
                class="negative-button font-semibold inline-block"
                data-te-modal-dismiss
                @click="modalStore.setModal(modalStore.backButtonLink)">
                {{ modalStore.backButtonText }}
              </button>
            </div>
            <div>
              <button
                v-if="modalStore.negativeButtonText"
                type="button"
                class="negative-button font-semibold inline-block justify-end hover:opacity-75"
                data-te-modal-dismiss
                @click="modalStore.negativeHandler">
                {{ modalStore.negativeButtonText }}
              </button>
              <button
                v-if="modalStore.positiveButtonText"
                type="button"
                class="positive-button rounded-md font-semibold px-4 py-2 ml-5 hover:opacity-75"
                :disabled="modalStore.positiveButtonDisabled"
                @click="modalStore.positiveHandleTrigger = true">
                {{ modalStore.positiveButtonText }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/components/modal";
</style>
